import { useEffect } from 'react';

import { useOktaAuth } from '@okta/okta-react';
import { useAtom, useAtomValue } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import type { RootStateOrAny } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { externalPmsSourceAtom } from '../../atoms/externalAtoms';
import { isMultiPracticeUser } from '../../modules/login/login-utils';
import { getPractice } from '../../modules/login/LoginActions';
import { useGetPractices } from '../../react-query/useGetPractices';

const directorPracticeIdStorage = createJSONStorage<string | null>(
  () => localStorage,
);

export const directorPracticeIdAtom = atomWithStorage(
  'jotai-director-practice-id',
  null,
  directorPracticeIdStorage,
);

export const useDirectorPracticeView = () => {
  const location = useLocation();
  const user = useSelector((state: RootStateOrAny) => state?.login?.user);
  const directorPracticeId = useAtomValue(directorPracticeIdAtom);

  const urlParams = new URLSearchParams(location.search);
  const directorViewPracticeId =
    urlParams.get('director_view_practice_id') || directorPracticeId;
  const canViewMultiplePractices = isMultiPracticeUser(user);

  return {
    isDirectorPracticeViewUser:
      canViewMultiplePractices && !!directorViewPracticeId,
    directorViewPracticeId,
  };
};

/**
 * This hook handles setting the temporary practice state for a director visiting
 * VideaAssist through the VideaInsights v1.5 link
 */
export const useDirectorPracticeViewState = () => {
  const user = useSelector((state: RootStateOrAny) => state?.login?.user);
  const dispatch = useDispatch();
  const location = useLocation();
  const authState = useOktaAuth()?.authState;
  const [directorPracticeId, setDirectorPracticeId] = useAtom(
    directorPracticeIdAtom,
  );
  const externalPmsSource = useAtomValue(externalPmsSourceAtom);
  const urlParams = new URLSearchParams(location.search);
  const directorViewPracticeIdParam = urlParams.get(
    'director_view_practice_id',
  );

  const { data: practices } = useGetPractices({});

  const canViewMultiplePractices = isMultiPracticeUser(user);

  // If we are a tableau director user hitting the deep link, set the practice id to the user's first practice so we can load preferences
  useEffect(() => {
    if (
      externalPmsSource === 'insights' &&
      canViewMultiplePractices &&
      !directorPracticeId &&
      practices?.pages?.[0].data?.[0]
    ) {
      setDirectorPracticeId(practices?.pages[0].data[0].id);
    }
  }, [
    canViewMultiplePractices,
    directorPracticeId,
    externalPmsSource,
    practices?.pages,
    setDirectorPracticeId,
    user?.organizations,
  ]);

  // When we have practice id params, set the director's practice id
  useEffect(() => {
    if (
      user &&
      authState?.isAuthenticated &&
      directorViewPracticeIdParam &&
      isMultiPracticeUser(user)
    ) {
      setDirectorPracticeId(directorViewPracticeIdParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has missing dependencies: 'authState?.accessToken?.accessToken', 'dispatch', and 'user'. Either include them or remove the dependency array.
  }, [directorViewPracticeIdParam, authState?.isAuthenticated, user.userRole]);

  // Fetch practice when director id changes
  useEffect(() => {
    if (directorPracticeId) {
      dispatch(
        getPractice(
          user.primaryOrganizationId,
          directorPracticeId,
          true,
          authState?.accessToken?.accessToken,
        ),
      );
    }
  }, [
    authState?.accessToken?.accessToken,
    directorPracticeId,
    dispatch,
    user.primaryOrganizationId,
  ]);
};
